import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, X } from 'lucide-react';

const RedeployBanner = ({ status }) => {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const totalPending = (status.businesses_pending || 0) + (status.events_pending || 0);
  const builtAt = status.built_at ? new Date(status.built_at).toLocaleString() : 'never';

  return (
    <div
      className="mb-6 bg-amber-500/10 border border-amber-500/40 rounded-xl p-4"
      data-testid="redeploy-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-amber-100 font-semibold text-lg">
              {totalPending} listing{totalPending !== 1 ? 's' : ''} need{totalPending === 1 ? 's' : ''} a redeploy
            </h3>
            <button
              onClick={() => setDismissed(true)}
              className="text-amber-300/70 hover:text-amber-100 p-1"
              aria-label="Dismiss"
              data-testid="dismiss-redeploy-banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-amber-100/80 mt-1">
            {status.businesses_pending > 0 && (
              <span><strong>{status.businesses_pending}</strong> approved business{status.businesses_pending !== 1 ? 'es' : ''}</span>
            )}
            {status.businesses_pending > 0 && status.events_pending > 0 && ' and '}
            {status.events_pending > 0 && (
              <span><strong>{status.events_pending}</strong> approved event{status.events_pending !== 1 ? 's' : ''}</span>
            )}
            {' '}have been added since the last production build ({builtAt}). Without a redeploy, their social-share previews (Facebook/LinkedIn/etc.) won't show the correct title, description, or image.
          </p>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <a
              href="https://app.emergent.sh/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-neutral-900 rounded-lg font-semibold text-sm transition-colors"
              data-testid="redeploy-cta"
            >
              <RefreshCw className="w-4 h-4" />
              Go to Emergent to Redeploy
            </a>
            <button
              onClick={() => setExpanded(e => !e)}
              className="inline-flex items-center gap-1 text-sm text-amber-200 hover:text-amber-100"
              data-testid="toggle-redeploy-details"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {expanded ? 'Hide list' : 'Show list'}
            </button>
          </div>

          {expanded && (
            <div className="mt-3 space-y-3" data-testid="redeploy-pending-list">
              {status.businesses_pending > 0 && (
                <div>
                  <p className="text-xs text-amber-200/80 font-semibold uppercase tracking-wide mb-1">Businesses</p>
                  <ul className="text-sm text-amber-100 space-y-1">
                    {(status.pending_business_slugs || []).map(b => (
                      <li key={b.slug} className="flex items-center gap-2">
                        <span>&bull;</span>
                        <span className="font-medium">{b.business_name}</span>
                        <code className="text-xs text-amber-300/70">/{b.slug}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {status.events_pending > 0 && (
                <div>
                  <p className="text-xs text-amber-200/80 font-semibold uppercase tracking-wide mb-1">Events</p>
                  <ul className="text-sm text-amber-100 space-y-1">
                    {(status.pending_event_slugs || []).map(e => (
                      <li key={e.slug} className="flex items-center gap-2">
                        <span>&bull;</span>
                        <span className="font-medium">{e.event_name}</span>
                        <code className="text-xs text-amber-300/70">/{e.slug}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RedeployBanner;
